import React from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import type { AddressType } from '@sweetgreen/garnish';
import { useBreakpointHelpers } from '@sweetgreen/garnish';

import {
  LocationSearch,
  LocationsMap,
  LocationWarningModal,
} from '@order/components';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { TransparentModal } from '../../../../components/TransparentModal';
import type { ModalStackChildProps } from '../../../../navigation/AppNavigation.props';
import { LocationLayout } from '../../../LocationsScreen/components';
import { LocationSearchTypePicker } from '../../../LocationsScreen/components/SearchBar/subcomponents';
import { useNavigationActions } from '../../../LocationsScreen/hooks';
import { useLocationsScreenData } from '../../../LocationsScreen/LocationsScreen.data';
import {
  useCancelReorder,
  useDismissReorder,
  useReorderState,
} from '../../hooks';
import {
  useReorderLeftIcon,
  useReorderRightIcon,
} from '../ReorderConfirmLocation/ReorderConfirmLocation.components';

export const ReorderChangeLocation = ({
  navigation,
}: ModalStackChildProps<'ReorderChangeLocation'>) => {
  useDismissReorder('locationSearch');
  const shouldUseLocationSearchComponent = useFeatureFlag(
    'XP-3273-short-location-search-component-in-reorder-enabled',
  );

  const { currentStep } = useReorderState();
  const cancelReorder = useCancelReorder();
  const { isMobile } = useBreakpointHelpers();
  const data = useLocationsScreenData();

  // The location search type picker is part of the header.
  const { onBack, onClose } = useNavigationActions();
  const leftIcon = useReorderLeftIcon();
  const rightIcon = useReorderRightIcon();
  const {
    locationSearchType,
    region,
    loading,
    showSearchAreaBtn,
    locations,
    selectedLocation,
    isShownLocationWarningModal,
    onLocationWarningPress,
    hideLocationWarningModal,
    fetchLocationsBasedOnUserGeolocation,
    setLocationSearchType,
    setSelectedLocation,
  } = data;
  const locationSearchTypePicker = useReorderSearchTypePicker({
    locationSearchType,
    setLocationSearchType,
    setSelectedLocation,
  });

  const isEmptyRegion = region.lat === 0 && region.lng === 0;

  React.useLayoutEffect(() => {
    navigation.setOptions({
      ...(shouldUseLocationSearchComponent
        ? {}
        : { headerTitle: () => locationSearchTypePicker }),
      headerLeft: onBack && isMobile ? () => leftIcon : undefined,
      headerRight: onClose && isMobile ? () => rightIcon : undefined,
      headerShown: isMobile,
    });
  }, [
    navigation,
    locationSearchTypePicker,
    leftIcon,
    rightIcon,
    isMobile,
    onBack,
    onClose,
    shouldUseLocationSearchComponent,
  ]);

  if (currentStep !== 'locationSearch' && Platform.OS === 'web') return null;

  return (
    <>
      <TransparentModal
        testID="reorder-change-location"
        disableAnimation={isMobile}
        size="large"
        onClose={cancelReorder}
      >
        {shouldUseLocationSearchComponent ? (
          <LocationSearch withSafeAreaInsets />
        ) : (
          <LocationLayout
            locationSearchTypePicker={locationSearchTypePicker}
            {...data}
          >
            <LocationsMap
              isLoadingMap={isEmptyRegion}
              isLoadingMapLocations={loading}
              isSearchAreaBtnEnabled={showSearchAreaBtn}
              onGeolocateUserBtnPress={fetchLocationsBasedOnUserGeolocation}
              {...data}
            />
          </LocationLayout>
        )}
      </TransparentModal>

      {isShownLocationWarningModal && !shouldUseLocationSearchComponent ? (
        <LocationWarningModal
          locations={locations}
          restaurantSlug={selectedLocation}
          onClose={hideLocationWarningModal}
          onConfirm={onLocationWarningPress}
        />
      ) : null}
    </>
  );
};

// For mobile header.
const useReorderSearchTypePicker = ({
  locationSearchType,
  setLocationSearchType,
  setSelectedLocation,
}: {
  readonly locationSearchType: AddressType;
  readonly setLocationSearchType: (
    value: React.SetStateAction<AddressType>,
  ) => void;
  readonly setSelectedLocation: (value: React.SetStateAction<string>) => void;
}) => {
  const { onBack, onClose } = useNavigationActions();
  const { isMobile } = useBreakpointHelpers();
  const leftIcon = useReorderLeftIcon();
  const rightIcon = useReorderRightIcon();
  const handleSelect = React.useCallback(
    (searchType: AddressType) => {
      setLocationSearchType(searchType);
      setSelectedLocation('');
    },
    [setLocationSearchType, setSelectedLocation],
  );

  return (
    <View style={styles.chipsContainer}>
      {!isMobile && onBack ? leftIcon : null}

      <LocationSearchTypePicker
        searchType={locationSearchType}
        onSelect={handleSelect}
      />

      {!isMobile && onClose ? rightIcon : null}
    </View>
  );
};

const styles = StyleSheet.create({
  chipsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
});
